import { useEffect, useRef } from "react";

type Delay = number | null;
type TimerHandler = (...args: any[]) => void;

/**
 * Provides a declarative useInterval
 *
 * @param callback - Function that will be called every `delay` ms.
 * @param delay - Number representing the delay in ms. Set to `null` to "pause" the interval.
 */

const useInterval = (callback: TimerHandler, delay: Delay) => {
  const savedCallbackRef = useRef<TimerHandler>();
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    savedCallbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    const handler = (...args: any[]) => savedCallbackRef.current!(...args);

    if (delay !== null) {
      timerRef.current = setInterval(handler, delay);
      return () => clearIntervalTimer();
    }
  }, [delay]);

  const clearIntervalTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current!);
      timerRef.current = null;
    }
  };

  return {
    clearIntervalTimer,
  };
};

export default useInterval;
